import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

export const Header = ({ onRequestItinerary }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (sectionId) => {
    setIsMobileMenuOpen(false);
    
    // If we're on the homepage, just scroll to section
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If we're on another page, navigate to home first, then scroll
      navigate('/');
      // Small delay to allow page to load before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-[#FAF9F7]/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://static.wixstatic.com/media/d66aed_4807542ab6c84dbbaec1a45677872d75~mv2.png/v1/fill/w_60,h_60,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/POMTT%2520LOGO%2520-PNG%2520white_edited.png"
              alt="POMTrips Logo"
              className="h-12 w-12 object-contain"
            />
            <span className="font-serif text-xl text-[#2C2C2C] hidden sm:block">POMTrips</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => handleNavigation('hero')} className="nav-link">{t('home')}</button>
            <button onClick={() => navigate('/tours')} className="nav-link">Tours</button>
            <button onClick={() => navigate('/heritage-walks')} className="nav-link">Heritage Walks</button>
            <button onClick={() => handleNavigation('destinations')} className="nav-link">{t('destinations')}</button>
            <button onClick={() => handleNavigation('about')} className="nav-link">{t('about')}</button>
            <button onClick={() => handleNavigation('contact')} className="nav-link">{t('contact')}</button>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 text-sm text-[#8B7D6B] hover:text-[#C4B5A0] transition-colors"
              aria-label="Toggle Language"
            >
              <Globe className="h-4 w-4" />
              <span className="uppercase font-medium">{language === 'en' ? 'ES' : 'EN'}</span>
            </button>

            {/* Request Itinerary Button - Desktop */}
            <Button 
              onClick={onRequestItinerary}
              className="hidden md:flex bg-[#C4B5A0] hover:bg-[#6B4E37] text-white transition-all duration-300"
            >
              {t('requestItinerary')}
            </Button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-[#2C2C2C] p-2"
              aria-label="Toggle Menu"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#FAF9F7] border-t border-[#F5F3F0]">
          <nav className="flex flex-col space-y-4 px-4 py-6">
            <button onClick={() => handleNavigation('hero')} className="mobile-nav-link">{t('home')}</button>
            <button onClick={() => navigate('/tours')} className="mobile-nav-link">Tours</button>
            <button onClick={() => navigate('/heritage-walks')} className="mobile-nav-link">Heritage Walks</button>
            <button onClick={() => handleNavigation('destinations')} className="mobile-nav-link">{t('destinations')}</button>
            <button onClick={() => handleNavigation('about')} className="mobile-nav-link">{t('about')}</button>
            <button onClick={() => handleNavigation('contact')} className="mobile-nav-link">{t('contact')}</button>
            <Button 
              onClick={() => {
                onRequestItinerary();
                setIsMobileMenuOpen(false);
              }}
              className="w-full bg-[#C4B5A0] hover:bg-[#6B4E37] text-white"
            >
              {t('requestItinerary')}
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};